import React, { useState, useEffect } from 'react';
import {
  Activity, Calendar, Clock, User, CheckCircle, AlertCircle,
  TrendingUp, Package, DollarSign, Eye, Play, Pause, Check, X, FileText,
  Filter, Search, RefreshCw
} from 'lucide-react';
import api, { API_URL } from '../../services/api';

function SuiviFabricationAmeliore() {
  const [ordres, setOrdres] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedOrdre, setSelectedOrdre] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadOrdres();
    const interval = setInterval(loadOrdres, 30000);
    return () => clearInterval(interval);
  }, [filtreStatut]);

  const loadOrdres = async () => {
    try {
      const params = filtreStatut !== 'all' ? { statut: filtreStatut } : {};
      const response = await api.get(`${API_URL}/atelier/fabrication`, { params });
      const ordresAvecAvancement = (response.data || []).map(ordre => ({
        ...ordre,
        pourcentage_avancement: calculerAvancement(ordre)
      }));
      setOrdres(ordresAvecAvancement);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculerAvancement = (ordre) => {
    if (!ordre.etapes || ordre.etapes.length === 0) return 0;
    const etapesTerminees = ordre.etapes.filter(e => e.date_fin != null).length;
    return Math.round((etapesTerminees / ordre.etapes.length) * 100);
  };

  const loadOrdreDetails = async (id) => {
    try {
      const response = await api.get(`${API_URL}/atelier/fabrication/${id}`);
      const ordreComplet = {
        ...response.data.ordre,
        etapes: response.data.etapes || [],
        composants: response.data.composants || [],
        temps_travail: response.data.temps_travail || [],
        pourcentage_avancement: calculerAvancement({ etapes: response.data.etapes || [] })
      };
      setSelectedOrdre(ordreComplet);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement des détails');
    }
  };

  const getStatutColor = (statut) => {
    const colors = {
      'en_attente': 'bg-gray-100 text-gray-800 border-gray-300',
      'en_cours': 'bg-orange-100 text-orange-800 border-orange-300',
      'termine': 'bg-green-100 text-green-800 border-green-300',
      'livre': 'bg-purple-100 text-purple-800 border-purple-300',
      'annule': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[statut] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatutIcon = (statut) => {
    switch(statut) {
      case 'en_attente': return <Clock className="h-5 w-5" />;
      case 'en_cours': return <Play className="h-5 w-5" />;
      case 'termine': return <CheckCircle className="h-5 w-5" />;
      case 'livre': return <Package className="h-5 w-5" />;
      case 'annule': return <AlertCircle className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const getEtapeStatutColor = (etape) => {
    if (etape.date_fin) {
      return 'bg-green-50 border-green-500';
    } else if (etape.date_debut && !etape.date_fin) {
      return 'bg-orange-50 border-orange-500';
    } else {
      return 'bg-gray-50 border-gray-300';
    }
  };

  const getEtapeStatutBadge = (etape) => {
    if (etape.date_fin) {
      return (
        <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
          <Check size={12} />
          Terminé
        </span>
      );
    } else if (etape.date_debut && !etape.date_fin) {
      return (
        <span className="px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1 animate-pulse">
          <Play size={12} />
          En cours
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 bg-gray-400 text-white text-xs font-bold rounded-full flex items-center gap-1">
          <Clock size={12} />
          En attente
        </span>
      );
    }
  };

  const getPrioriteColor = (priorite) => {
    const colors = {
      'urgente': 'bg-red-500',
      'haute': 'bg-orange-500',
      'normale': 'bg-blue-500',
      'basse': 'bg-gray-500'
    };
    return colors[priorite] || 'bg-gray-500';
  };

  const stats = {
    en_attente: ordres.filter(o => o.statut === 'en_attente').length,
    en_cours: ordres.filter(o => o.statut === 'en_cours').length,
    termine: ordres.filter(o => o.statut === 'termine').length,
    retard: ordres.filter(o => {
      if (!o.date_livraison_prevue) return false;
      return new Date(o.date_livraison_prevue) < new Date() && o.statut !== 'livre';
    }).length
  };

  const ordresFiltres = ordres.filter(ordre => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        ordre.reference_bijou?.toLowerCase().includes(term) ||
        ordre.numero_ordre?.toString().includes(term) ||
        ordre.client_nom?.toLowerCase().includes(term) ||
        ordre.type_bijou?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-xl p-6 text-white">
        <h1 className="text-3xl font-bold flex items-center space-x-3">
          <Activity className="h-8 w-8" />
          <span>Suivi de Fabrication</span>
        </h1>
        <p className="mt-2 text-indigo-100">Suivez l'avancement de tous vos ordres de fabrication en temps réel</p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En Attente</p>
              <p className="text-3xl font-bold text-gray-700">{stats.en_attente}</p>
            </div>
            <Clock className="h-12 w-12 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En Cours</p>
              <p className="text-3xl font-bold text-orange-600">{stats.en_cours}</p>
            </div>
            <Play className="h-12 w-12 text-orange-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Terminés</p>
              <p className="text-3xl font-bold text-green-600">{stats.termine}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En Retard</p>
              <p className="text-3xl font-bold text-red-600">{stats.retard}</p>
            </div>
            <AlertCircle className="h-12 w-12 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filtres et Recherche */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Recherche */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par référence, numéro, client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtres par statut */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFiltreStatut('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filtreStatut === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setFiltreStatut('en_attente')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filtreStatut === 'en_attente'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Clock size={16} />
              En Attente
            </button>
            <button
              onClick={() => setFiltreStatut('en_cours')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filtreStatut === 'en_cours'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Play size={16} />
              En Cours
            </button>
            <button
              onClick={() => setFiltreStatut('termine')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                filtreStatut === 'termine'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CheckCircle size={16} />
              Terminés
            </button>
            <button
              onClick={loadOrdres}
              className="px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Liste des ordres */}
      <div className="space-y-4">
        {ordresFiltres.map((ordre) => {
          const isRetard = ordre.date_livraison_prevue && 
                          new Date(ordre.date_livraison_prevue) < new Date() && 
                          ordre.statut !== 'livre';
          
          return (
            <div 
              key={ordre.id} 
              className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden ${
                isRetard ? 'border-2 border-red-500' : ''
              }`}
            >
              <div className={`h-2 ${getPrioriteColor(ordre.priorite)}`}></div>

              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-800">{ordre.reference_bijou}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 flex items-center space-x-1 ${getStatutColor(ordre.statut)}`}>
                        {getStatutIcon(ordre.statut)}
                        <span className="capitalize">{ordre.statut.replace('_', ' ')}</span>
                      </span>
                      {ordre.priorite && ordre.priorite !== 'normale' && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${getPrioriteColor(ordre.priorite)}`}>
                          {ordre.priorite.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      Ordre #{ordre.numero_ordre} - {ordre.type_bijou?.replace('_', ' ').toUpperCase()}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-3xl font-bold text-indigo-600">
                      {ordre.pourcentage_avancement || 0}%
                    </div>
                    <div className="text-xs text-gray-500">Avancement</div>
                  </div>
                </div>

                {/* Barre de progression */}
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${ordre.pourcentage_avancement || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Aperçu des étapes avec codes couleur */}
                {ordre.etapes && ordre.etapes.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Étapes:</div>
                    <div className="flex flex-wrap gap-2">
                      {ordre.etapes.map((etape, idx) => (
                        <div
                          key={idx}
                          className={`px-3 py-1 rounded-lg text-xs font-medium border-2 ${getEtapeStatutColor(etape)}`}
                        >
                          {etape.nom_etape}
                          {etape.date_fin && <Check className="inline ml-1 h-3 w-3 text-green-600" />}
                          {etape.date_debut && !etape.date_fin && <Play className="inline ml-1 h-3 w-3 text-orange-600" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Informations */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {ordre.client_nom && (
                    <div className="flex items-center space-x-2 text-sm">
                      <User className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="text-gray-500 text-xs">Client</div>
                        <div className="font-semibold text-gray-800">{ordre.client_nom}</div>
                      </div>
                    </div>
                  )}

                  {ordre.date_livraison_prevue && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="text-gray-500 text-xs">Livraison prévue</div>
                        <div className={`font-semibold ${isRetard ? 'text-red-600' : 'text-gray-800'}`}>
                          {new Date(ordre.date_livraison_prevue).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  )}

                  {ordre.prix_estime && (
                    <div className="flex items-center space-x-2 text-sm">
                      <DollarSign className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="text-gray-500 text-xs">Prix estimé</div>
                        <div className="font-semibold text-gray-800">
                          {parseFloat(ordre.prix_estime).toLocaleString()} DH
                        </div>
                      </div>
                    </div>
                  )}

                  {ordre.complexite && (
                    <div className="flex items-center space-x-2 text-sm">
                      <TrendingUp className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="text-gray-500 text-xs">Complexité</div>
                        <div className="font-semibold text-gray-800 capitalize">
                          {ordre.complexite.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {ordre.description && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="text-sm text-gray-700">{ordre.description}</div>
                  </div>
                )}

                {isRetard && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded p-3 mb-4">
                    <div className="flex items-center space-x-2 text-red-800">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-semibold">⚠️ Retard de livraison</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <button 
                    onClick={() => loadOrdreDetails(ordre.id)}
                    className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors flex items-center space-x-2 cursor-pointer"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Voir Détails</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {ordresFiltres.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Activity className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <p className="text-xl text-gray-500">
            {searchTerm ? 'Aucun résultat trouvé' : 'Aucun ordre de fabrication'}
          </p>
        </div>
      )}

      {/* Modal de détails avec étapes en couleur */}
      {showDetailModal && selectedOrdre && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex justify-between items-center sticky top-0">
              <h3 className="text-xl font-bold">Ordre #{selectedOrdre.numero_ordre} - {selectedOrdre.reference_bijou}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations générales */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-indigo-600" />
                  Informations générales
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Référence</div>
                    <div className="font-bold text-indigo-600 text-lg">{selectedOrdre.reference_bijou || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Type</div>
                    <div className="font-semibold text-gray-800 capitalize">
                      {selectedOrdre.type_bijou ? selectedOrdre.type_bijou.replace('_', ' ') : 'Non spécifié'}
                    </div>
                  </div>
                  {selectedOrdre.client_nom && (
                    <div>
                      <div className="text-sm text-gray-600">Client</div>
                      <div className="font-semibold text-gray-800">{selectedOrdre.client_nom}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-sm text-gray-600">Avancement</div>
                    <div className="font-bold text-indigo-600 text-lg">{selectedOrdre.pourcentage_avancement || 0}%</div>
                  </div>
                  {selectedOrdre.description && (
                    <div className="col-span-2">
                      <div className="text-sm text-gray-600">Description</div>
                      <div className="text-gray-800 mt-1">{selectedOrdre.description}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Étapes de fabrication avec codes couleur */}
              {selectedOrdre.etapes && selectedOrdre.etapes.length > 0 && (
                <div className="bg-white border-2 border-indigo-200 p-4 rounded-lg">
                  <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-indigo-600" />
                    Évolution des Étapes de Fabrication
                  </h4>
                  <div className="space-y-3">
                    {selectedOrdre.etapes.map((etape, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-start space-x-3 p-4 rounded-lg border-l-4 ${getEtapeStatutColor(etape)} transition-all`}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {etape.date_fin ? (
                            <CheckCircle className="h-8 w-8 text-green-600" />
                          ) : etape.date_debut && !etape.date_fin ? (
                            <Play className="h-8 w-8 text-orange-600 animate-pulse" />
                          ) : (
                            <Clock className="h-8 w-8 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-bold text-gray-900 text-lg">{etape.nom_etape}</div>
                            {getEtapeStatutBadge(etape)}
                          </div>
                          {etape.ouvrier_nom && (
                            <div className="text-sm text-gray-700 flex items-center mt-2 bg-white px-3 py-1 rounded-full inline-flex">
                              <User className="h-4 w-4 mr-2 text-indigo-600" />
                              <span className="font-medium">{etape.ouvrier_nom}</span>
                            </div>
                          )}
                          {etape.date_debut && (
                            <div className="text-sm text-gray-600 mt-3 flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Début: {new Date(etape.date_debut).toLocaleDateString('fr-FR')}
                              </span>
                              {etape.date_fin && (
                                <span className="flex items-center gap-1 text-green-700 font-medium">
                                  <Check className="h-4 w-4" />
                                  Fin: {new Date(etape.date_fin).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuiviFabricationAmeliore;
